import { Clipboard } from '@angular/cdk/clipboard'
import { booleanAttribute, Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { accessibleColor } from 'se-fe-color'
import { SeFeToastService } from 'se-fe-toast'
import { RegistrationCardTranslationsService } from '../registration-card-translations.service'

@Component({
  selector: 'se-open-registration-card',
  templateUrl: './registration-card.component.html',
  styleUrls: ['./registration-card.component.scss'],
  standalone: false,
})
export class RegistrationCardComponent implements OnInit {
  private readonly translateKey = 'SE_PO_SE_REGISTRATION_UI_REGISTRATION_CARD.'
  private readonly copyLinkErrorFallback = 'Unable to copy link'
  private readonly copyLinkSuccessFallback = 'Link copied to clipboard'

  @Input() public orgName = ''
  @Input() public orgLogoUrl = ''
  @Input() public orgColor = ''
  @Input() public registrationName = ''
  @Input() public sport = ''
  @Input() public type = ''
  @Input() public avatarUrl = ''
  @Input() public backgroundImgUrl = ''
  @Input() public infoHeaderText = ''
  @Input() public infoText = ''
  @Input() public buttonLinkUrl = ''
  @Input({ transform: booleanAttribute }) public showShareAction = true
  @Input() public buttonText = ''
  @Input() public cardRouterLink: string | unknown[] = ''
  @Input() public cardRouterQueryParams: Record<string, string | number> = {}
  @Input() public orderRouterLinkText = ''
  @Input() public orderRouterLink: string | unknown[] = ''
  @Input() public orderRouterQueryParams: Record<string, string | number> = {}

  @Output() public primaryActionClick = new EventEmitter<void>()
  @Output() public shareActionClick = new EventEmitter<void>()

  constructor(
    private clipboard: Clipboard,
    private seToast: SeFeToastService,
    private translateService: TranslateService,
    private registrationCardTranslations: RegistrationCardTranslationsService
  ) {}

  public get hasCardRouterLink(): boolean {
    return this.hasRouterLink(this.cardRouterLink)
  }

  public get hasOrderRouterLink(): boolean {
    return this.hasRouterLink(this.orderRouterLink)
  }

  public async ngOnInit(): Promise<void> {
    await this.registrationCardTranslations.load()
  }

  public get backgroundImage(): string {
    if (!this.backgroundImgUrl && !this.orgColor) {
      return ''
    }

    const color = this.orgColor ? accessibleColor(this.orgColor, '#FFFFFF') : undefined
    const overlay = this.orgColor
      ? `linear-gradient(90deg, rgba(30, 13, 13, .82), ${color})`
      : 'linear-gradient(90deg, rgba(30, 13, 13, .82), rgba(166, 12, 16, .84))'

    return this.backgroundImgUrl ? `${overlay}, url("${this.backgroundImgUrl}")` : overlay
  }

  public get shareLabel(): string {
    const registrationName = this.registrationName || this.translateService.instant(`${this.translateKey}SHARE.fallback`)

    return this.translateService.instant(`${this.translateKey}SHARE.label`, { registrationName })
  }

  public get cardLinkLabel(): string {
    const labelKey = this.registrationName ? 'label' : 'fallback'

    return this.translateService.instant(`${this.translateKey}CARD_LINK.${labelKey}`, { registrationName: this.registrationName })
  }

  public get orderLinkText(): string {
    if (this.orderRouterLinkText) {
      return this.orderRouterLinkText
    }
    return this.translateService.instant(`${this.translateKey}ORDER.details`)
  }

  public handlePrimaryActionClick(): void {
    this.primaryActionClick.emit()
    window.open(this.buttonLinkUrl, '_blank', 'noopener,noreferrer')
  }

  public handleShareActionClick(): void {
    this.shareActionClick.emit()
    this.copyLinkToClipboard()
  }

  public copyLinkToClipboard(): void {
    if (!this.buttonLinkUrl) {
      return
    }

    if (this.clipboard.copy(this.buttonLinkUrl)) {
      this.seToast.done(
        this.translateWithFallback(`${this.translateKey}COPY_LINK.success`, this.copyLinkSuccessFallback)
      )
      return
    }

    this.seToast.error(
      this.translateWithFallback(`${this.translateKey}COPY_LINK.error`, this.copyLinkErrorFallback)
    )
  }

  private translateWithFallback(key: string, fallback: string): string {
    const translated = this.translateService.instant(key)

    return typeof translated === 'string' && translated !== key ? translated : fallback
  }

  private hasRouterLink(routerLink: string | unknown[]): boolean {
    if (Array.isArray(routerLink)) {
      return routerLink.length > 0
    }

    return typeof routerLink === 'string' && routerLink.trim().length > 0
  }
}
